import type { SlideProps } from "@/engine/app/offsite-config";
import { usePhase } from "@/state/store";
import { useOvernightStatus } from "@/hooks/useOvernightStatus";
import { CodeKulisse } from "./_code-kulisse";

/**
 * Generierung — die KI baut aus jedem Prompt einen Prototyp.
 *
 * Lokale Kopie von offsites/ar/slides/SlideSpikes.tsx. Eigene Folie, weil
 * hier wieder etwas anderes zählt: nicht die Zeit (die läuft nicht mehr) und
 * nicht der QR-Code, sondern der Fortschritt. Diese Folie steht, während der
 * Moderator daneben SVO OS zeigt — sie muss aus dem Augenwinkel beantworten,
 * wie weit es ist.
 *
 * Deshalb Kästchen statt Balken: Ein Balken bei 50% sagt „halb". Kästchen,
 * von denen die Hälfte gefüllt ist, sagen „die Hälfte der eigenen Prompts
 * steht" — und die Bühnengruppe sieht, dass es um die eigenen Prompts geht.
 */
/**
 * Bezugsgröße für den Füllstand. Die Prototypen der Probeläufe lagen zwischen
 * 6.000 und 14.000 Zeichen; 10.000 trifft die Mitte. Ein Balken, der zu früh
 * volläuft, wird bei 92% gekappt — fertig ist erst fertig.
 */
const ERWARTETE_ZEICHEN = 10_000;

export function SlideGenerierung(_: SlideProps) {
  const phase = usePhase();
  const status = useOvernightStatus(phase === "overnight" || phase === "reveal");

  /**
   * Ein Kästchen je abgeschicktem Prompt — nicht auf Verdacht geschätzt.
   *
   * Die Teamnummern kommen vom Endpunkt, gefiltert auf die laufende Gruppe
   * und auf tatsächlich abgeschickte Prompts.
   */
  const nummern = status?.teams ?? [];
  const gesamt = nummern.length > 0 ? nummern.length : (status?.total ?? 0);
  const fertigeNummern = new Set(status?.fertig ?? []);
  const fertig = status?.done ?? 0;
  const komplett =
    status?.status === "complete" || (status != null && gesamt > 0 && fertig >= gesamt);

  return (
    <div className="deck-root ar-slide ar-spikes" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />
      <CodeKulisse spalten={3} />

      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          {komplett ? "Fertig" : "Es wird gebaut"}
        </div>
        <h1 className="deck-title rise rise-2">
          {komplett ? "Eure Prototypen stehen" : "Aus euren Worten wird Software"}
        </h1>
      </header>

      <div className="ar-spikes-mitte rise rise-3">
        {status && gesamt === 0 && (
          <p className="ar-spikes-zahl" data-testid="spikes-leer">
            Noch kein Prompt abgeschickt.
          </p>
        )}
        <div className="ar-spikes-kaesten" data-testid="spikes-kaesten">
          {/* Je Prompt ein Kästchen, in der Reihenfolge der Teamnummern. Die
           *  Lambda baut alle gleichzeitig — es gibt also kein „das dritte ist
           *  dran", sondern fertig oder noch nicht. */}
          {(nummern.length > 0
            ? nummern
            : Array.from({ length: gesamt }, (_, i) => i + 1)
          ).map((nr) => {
            const istFertig = fertigeNummern.has(nr);
            const zeichen = status?.fortschritt?.[nr] ?? 0;
            /**
             * Füllhöhe aus echten Zeichen, nicht aus der Zeit.
             *
             * Die Endlänge kennt niemand vorher, deshalb ein Erfahrungswert
             * als Bezug — und gekappt bei 92%: Der letzte Rest gehört dem
             * fertigen Prototyp, sonst stünde ein Kästchen bei 100% und wäre
             * trotzdem nicht fertig.
             */
            const anteil = istFertig
              ? 1
              : Math.min(0.92, zeichen / ERWARTETE_ZEICHEN);
            return (
              <span
                key={nr}
                className={`ar-spikes-kasten${istFertig ? " is-fertig" : ""}${
                  !istFertig && zeichen > 0 ? " is-dran" : ""
                }`}
                data-testid={`spikes-kasten-${nr}`}
                aria-hidden="true"
              >
                <span
                  className="ar-spikes-fuellung"
                  style={{ height: `${Math.round(anteil * 100)}%` }}
                />
                {istFertig && (
                  <svg
                    className="ar-spikes-haken"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 12.5 9.5 18 20 6.5" />
                  </svg>
                )}
              </span>
            );
          })}
        </div>

        <p className="ar-spikes-zahl" data-testid="spikes-zahl">
          {status ? (
            komplett ? (
              <>
                <strong>{fertig}</strong> Prototypen fertig
              </>
            ) : (
              <>
                <strong>{fertig}</strong> von <strong>{gesamt}</strong> fertig
              </>
            )
          ) : (
            "Fortschritt wird geladen …"
          )}
        </p>
      </div>

      <footer className="ar-spikes-fuss rise rise-4">
        <p className="ar-arbeit-hinweis">
          {komplett
            ? "Gleich seht ihr, was daraus geworden ist."
            : "Jeder Prompt wird einzeln gebaut — echte Software, kein Video."}
        </p>
      </footer>
    </div>
  );
}
